import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { clientApi } from "../../api";
import timeAgo from "../../utils/timeAgo";

/**
 * Surface "when was the provider cache last refreshed?" on any report page.
 * Admins get a "Sync now" button that kicks the sync in the background;
 * staff see the freshness info only.
 *
 * Pass a `cache` prop from the report's API response if you want to
 * render instantly without a second round-trip (or omit and the banner
 * fetches it itself). Pass `provider` ("s7000" | "igt") so the wording names
 * the venue's actual provider — defaults to S7000 (the only pages that omit
 * it are S7000-only reports).
 */
export default function SyncStatusBanner({ cache: cacheProp, provider }) {
  const { user } = useAuth();
  const isAdmin = user?.clientRole === "admin";
  const providerLabel = { igt: "IGT", swiftpos: "SwiftPOS", circlescan: "CircleScan", s7000: "S7000" }[provider] || "S7000";
  const [state, setState] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (cacheProp) {
      setState({ s7000_members: {
        last_sync_at:     cacheProp.last_sync_at,
        last_sync_status: cacheProp.last_sync_status,
        row_count:        cacheProp.row_count,
      } });
    } else {
      reload();
    }
    // eslint-disable-next-line
  }, [cacheProp?.last_sync_at]);

  async function reload() {
    try { setState(await clientApi.getSyncStatus()); }
    catch (err) { setError(err.message); }
  }

  async function syncNow() {
    setSyncing(true); setError("");
    try {
      await clientApi.syncNow();
      // Poll briefly to reflect the new row count.
      const poll = async (n = 0) => {
        await new Promise((r) => setTimeout(r, 1500));
        await reload();
        const st = (await clientApi.getSyncStatus()).s7000_members;
        if (st?.last_sync_status === "running" && n < 20) return poll(n + 1);
        setSyncing(false);
      };
      poll();
    } catch (err) {
      setError(err.message);
      setSyncing(false);
    }
  }

  // The sync-state object is keyed by provider (e.g. s7000_members). Prefer the
  // S7000 key for back-compat, but fall back to whichever *_members-shaped entry
  // is present so a non-S7000 venue doesn't wrongly read "never synced".
  const m = state?.s7000_members
    || (state && Object.values(state).find((v) => v && typeof v === "object" && "last_sync_at" in v))
    || null;
  const lastSync = m?.last_sync_at ? new Date(m.last_sync_at) : null;
  const status = m?.last_sync_status || (lastSync ? "ok" : "never");

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: "0.75rem", padding: "0.5rem 0.85rem", borderRadius: "var(--radius-md)",
      background: "var(--bg-elevated)", border: "1px solid var(--border)",
      marginBottom: "1rem", fontSize: "0.85rem",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <StatusDot status={syncing ? "running" : status} />
        <span>
          {syncing ? (
            <>Syncing from {providerLabel}…</>
          ) : !lastSync ? (
            <>{providerLabel} cache has never been synced for this venue.</>
          ) : (
            <>
              Data from local cache · last synced {timeAgo(lastSync)}
              {m?.row_count ? ` · ${m.row_count.toLocaleString()} members` : ""}
              {status === "error" && <span style={{ color: "var(--danger)", marginLeft: "0.5rem" }}>({m.last_sync_error || "error"})</span>}
            </>
          )}
        </span>
      </div>
      {isAdmin && (
        <button
          className="btn btn-outline btn-sm"
          onClick={syncNow}
          disabled={syncing}
        >
          {syncing ? "Syncing…" : "Sync now"}
        </button>
      )}
      {error && <span style={{ color: "var(--danger)" }}>{error}</span>}
    </div>
  );
}

function StatusDot({ status }) {
  const color = status === "running" ? "var(--warning)"
              : status === "ok"      ? "var(--success)"
              : status === "error"   ? "var(--danger)"
              : "var(--text-muted)";
  return <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 999, background: color }} />;
}

